import { useCallback, useEffect } from "react";
import { chatService } from "api/chat-service";

export const useChatReadState = ({
  chatId,
  messages,
  user,
  currentLoginUserId,
  doctorType,
  readStateRef,
  lastSeenIncomingMessageIdRef,
  pollMessagesPage,
  shouldAutoScrollRef,
  scrollToBottom,
}) => {
  const handleMarkChatIsRead = useCallback(
      async ({ force = false } = {}) => {
        if (!chatId) return;
        if (readStateRef.current.inFlight) return;
        if (!force && readStateRef.current.lastMarkedChatId === chatId) return;
        readStateRef.current.inFlight = true;
        try {
          await chatService.updateChatRead(chatId);
          readStateRef.current.lastMarkedChatId = chatId;
        } catch (error) {
          if (error?.response?.status !== 401) {
            console.error("Failed to mark chat as read", error);
          }
        } finally {
          readStateRef.current.inFlight = false;
        }
      },
      [chatId, readStateRef]
  );

  const pollLatestMessages = useCallback(async () => {
    if (!chatId) return;
    const response = await pollMessagesPage();
    if (!response?.hasNewMessages) return;
    if (shouldAutoScrollRef.current) {
      requestAnimationFrame(() => scrollToBottom());
    }
    await handleMarkChatIsRead({ force: true });
  }, [chatId, handleMarkChatIsRead, pollMessagesPage, scrollToBottom, shouldAutoScrollRef]);

  useEffect(() => {
    if (!chatId || !messages.length) return;
    const latestMessage = messages[messages.length - 1];
    const latestMessageId = latestMessage?.id;
    if (!latestMessageId) return;
    const isOwnLatestMessage = latestMessage?.doctor?.id === currentLoginUserId && doctorType;
    if (isOwnLatestMessage) return;
    if (lastSeenIncomingMessageIdRef.current === latestMessageId) return;
    lastSeenIncomingMessageIdRef.current = latestMessageId;
    handleMarkChatIsRead({ force: true });
  }, [chatId, currentLoginUserId, doctorType, handleMarkChatIsRead, lastSeenIncomingMessageIdRef, messages]);

  useEffect(() => {
    if (!user || !chatId) return;
    const interval = setInterval(() => {
      pollLatestMessages();
    }, 3000);
    return () => clearInterval(interval);
  }, [user, chatId, pollLatestMessages]);

  return { handleMarkChatIsRead, pollLatestMessages };
};
